import React, { useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface DataProtectionProviderProps {
  children: React.ReactNode;
  mode?: "strict" | "relaxed" | "off";
  allowCopyInInputs?: boolean;
  className?: string;
}

/**
 * DataProtectionProvider - Proteção anti-cópia de dados sensíveis
 * 
 * Modos:
 * - strict: Bloqueia todas as formas de cópia (para Partner Portal)
 * - relaxed: Permite algumas operações em inputs (para Org Portal)
 * - off: Desativa todas as proteções
 * 
 * Limitações conhecidas:
 * - Usuários avançados podem desabilitar JavaScript
 * - DevTools pode ser aberto antes do carregamento
 * - Print Screen não pode ser bloqueado no navegador
 * - A segurança real está no servidor (RLS)
 */
export function DataProtectionProvider({ 
  children, 
  mode = "strict",
  allowCopyInInputs = true,
  className 
}: DataProtectionProviderProps) {
  
  // Bloquear atalhos de teclado
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (mode === "off") return;
    
    const target = e.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' || 
                         target.tagName === 'TEXTAREA' || 
                         target.isContentEditable;
    
    // Em modo relaxed, permitir atalhos em inputs
    if (mode === "relaxed" && isInputField && allowCopyInInputs) {
      return;
    }
    
    // Bloquear Ctrl+C, Ctrl+V, Ctrl+X (copy/paste/cut)
    if (e.ctrlKey || e.metaKey) {
      if (['c', 'v', 'x'].includes(e.key.toLowerCase())) {
        // Permitir em inputs se configurado
        if (isInputField && allowCopyInInputs) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Bloquear Ctrl+U (view source)
      if (e.key.toLowerCase() === 'u') {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Bloquear Ctrl+P (print)
      if (e.key.toLowerCase() === 'p') {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Bloquear Ctrl+S (save page)
      if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Bloquear Ctrl+Shift+I e Ctrl+Shift+C (DevTools)
      if (e.shiftKey && ['i', 'c', 'j'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    
    // Bloquear F12 (DevTools)
    if (e.key === 'F12') {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [mode, allowCopyInInputs]);
  
  // Bloquear menu de contexto (botão direito)
  const handleContextMenu = useCallback((e: MouseEvent) => {
    if (mode === "off") return;
    
    const target = e.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' || 
                         target.tagName === 'TEXTAREA' || 
                         target.isContentEditable;
    
    // Permitir em inputs se configurado
    if (isInputField && allowCopyInInputs) {
      return;
    }
    
    e.preventDefault();
  }, [mode, allowCopyInInputs]);
  
  // Bloquear eventos de cópia
  const handleCopy = useCallback((e: ClipboardEvent) => {
    if (mode === "off") return;
    
    const target = e.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' || 
                         target.tagName === 'TEXTAREA' || 
                         target.isContentEditable;
    
    // Permitir em inputs se configurado
    if (isInputField && allowCopyInInputs) {
      return;
    }
    
    e.preventDefault();
  }, [mode, allowCopyInInputs]);
  
  // Bloquear seleção de texto via drag
  const handleDragStart = useCallback((e: DragEvent) => {
    if (mode === "off") return;
    
    const target = e.target as HTMLElement;
    
    // Permitir drag de imagens e links
    if (target.tagName === 'IMG' || target.tagName === 'A') {
      return;
    }
    
    e.preventDefault();
  }, [mode]);
  
  useEffect(() => {
    if (mode === "off") return;
    
    // Adicionar event listeners
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, [mode, handleKeyDown, handleContextMenu, handleCopy, handleDragStart]);
  
  if (mode === "off") {
    return <>{children}</>;
  }
  
  return (
    <div 
      className={cn("data-protected", className)}
      style={{
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {children}
    </div>
  );
}
